import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ChallengesService } from '../../model/challenges/challenges.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { PageChangedEvent } from 'ngx-bootstrap/pagination';
import { CommonStatus, Globals, SearchedDate } from '../../globals/globals';
import { AuthService } from '../../model/auth-model/auth.service';
import { TextDateSearchModel } from 'src/app/shared/modules/search/models/text-date-search-model';
import { GlobalService } from 'src/app/util/service/global.service';
import { Translations } from 'src/app/util/service/internationalization/translations.service';
import { ChallengeCategory, IChallengeCategory } from './models/challenge-category';
import SystemConstants from 'src/app/util/constants/SystemConstants';
import { LocalizationService, TranslationParams } from 'src/app/util/service/internationalization/localization.service';
import { CustomTypeAttribute, IGlobals } from 'src/app/globals/custom-types/global-custom-types';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { CommonValidationService, CustomValidatorOptions } from 'src/app/util/service/common-validation.service';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'app-manage-challenge-categories',
    templateUrl: '../../view/manage-challenge-categories/manage-challenge-categories.component.html',
    styleUrls: ['../../view/manage-challenge-categories/manage-challenge-categories.component.scss'],
    standalone: false
})
export class ManageChallengeCategoriesComponent implements OnInit {
  challengeCategoryList: ChallengeCategory[] = []
  sAttributes: CustomTypeAttribute<IGlobals['adminPaginationAttrs'], any>;
  addEditCategoriesForm: UntypedFormGroup;
  isCreatedDateVisible: boolean;
  searchDetails: TextDateSearchModel = new TextDateSearchModel();
  modalRef : BsModalRef
  currentPage:number;
  isClearValue: boolean;
  isNoRecordHide: boolean;
  limitForPage: number = SystemConstants.DEFAULT_LIMIT_OF_PAGE_ITEMS;
  constructor(
    private formBuilder: UntypedFormBuilder,
    private challengeModelService: ChallengesService,
    private toastr: ToastrService,
    public globals: Globals,
    private authService: AuthService,
    private globalService: GlobalService,
    public translations: Translations,
    private commonValidationService: CommonValidationService,
    private localizationService: LocalizationService,
    private modalService: BsModalService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  get challengeCategoryName(): AbstractControl { return this.addEditCategoriesForm.controls.name; }
  
  get challengeCategoryStatus(): AbstractControl { return this.addEditCategoriesForm.controls.status; }

  ngOnInit() {
    this.challengeCategoryFormInit();
    this.authService.isLoggedIn.subscribe((isLogin: boolean) => {
      if (isLogin) {
        this.sAttributes = Object.assign({}, this.globals.adminPaginationAttrs);
        this.getChallengeCategoryListAndCounts();
      }
    }, err => {
      console.log(err);
    });
  }


  challengeCategoryFormInit(): void {
    this.addEditCategoriesForm = this.formBuilder.group({
      // name: ['', this.commonValidationService.setCustomValidatorMethods({minLength: 2, maxLength: 25})],
      name: ['', Validators.required],
      status: [CommonStatus.ACTIVE],
    });
  }

  searchByDate(date: Date | Date[]) {
    this.closeDialogBox()
    this.searchDetails.date = date;
    this.resetPageUrl()
    this.getChallengeCategoryListAndCounts();
  }

  searchByText(text: string) {
    this.closeDialogBox()
    this.searchDetails.text = text;
    this.resetPageUrl()
    this.getChallengeCategoryListAndCounts();
  }

  /**
   * This is used to add search fields in query
   * @param queryObj IChallengeCategory
   */
  setSearchFields(queryObj: IChallengeCategory): IChallengeCategory {
    this.setNameQuery(queryObj);
    this.setCreatedDateQuery(queryObj);
    return queryObj;
  }

  setNameQuery(query: IChallengeCategory): void {
    if (this.searchDetails && this.searchDetails.text) {
      query.name = this.searchDetails.text;
    }
  }

  setCreatedDateQuery(query: IChallengeCategory): void {
    if (this.searchDetails && this.searchDetails.date && (this.searchDetails.date as Date[]).length === 2) {
      query.createdDate = JSON.stringify(new SearchedDate(this.searchDetails.date[0], this.searchDetails.date[1]));
    }
  }

  clearSearchValue(){
    this.searchDetails.text = '';
  }

  /**
   * This is used to handle multi req handle
   **/
  getChallengeCategoryListAndCounts(): void {
    forkJoin([
      this.getChallengeCategoryList(this.sAttributes.limits, this.sAttributes.skips),
      this.getChallengeCategoryCounts(),
    ]);
  }


   onChallengeCategorySubmit(): void {
    if (this.addEditCategoriesForm.invalid) {
      this.sAttributes.submitted = true;
      return;
    }
    this.clearSearchValue();
    const query = this.addEditCategoriesForm.value;
    query.status = Number(query.status);
    this.challengeModelService.createChallengCategory(query).subscribe(res => {
      if (res && res.status === SystemConstants.OKAY_MSG) {
        this.localizationService.displayToastrSuccess(this.toastr, this.translations.GLOBAL.SUCCESS_MESSAGES.CREATED);
        this.currentPage = SystemConstants.DEFAULT_PAGE_NUMBER;
        this.getChallengeCategoryListAndCounts();
        const customData = res?.data?.ops?.[0] || res?.data;
        if (customData) {
          this.challengeCategoryList.splice(0, 0, customData);
          this.toggleForm();
        }
      } else {
        this.toastr.error(SystemConstants.ERROR, res.errMsg);
      }
    }, err => {
      if (err && err.error && err.error.error && err.error.error.message) {
        this.toastr.error(SystemConstants.ERROR, err.error.error.message);
      }
    });
  }


  updateChallengeCategory(): void {
    if (this.addEditCategoriesForm.invalid) {
      this.sAttributes.submitted = true;
      return;
    }
    const query = this.addEditCategoriesForm.value;
    query.status = Number(query.status);
    const parmsObj: string = this.sAttributes.itemArray._id;
    this.challengeModelService.updateChallengeCategory(parmsObj, query).subscribe(res => {
      if (res && res.status === SystemConstants.OKAY_MSG) {
        this.localizationService.displayToastrSuccess(this.toastr, this.translations.GLOBAL.SUCCESS_MESSAGES.UPDATED);
        const getIndex: number = this.challengeCategoryList.findIndex((x) => String(x._id) === String(parmsObj));
        if (getIndex >= 0) {
          if (query.name) {
            this.sAttributes.itemArray.name = query.name;
          }
          if (query.icon) {
            this.sAttributes.itemArray.icon = query.icon;
          }
          if (query.status) {
            this.sAttributes.itemArray.status = Number(query.status);
          }
          this.challengeCategoryList[getIndex] = Object.assign({}, this.sAttributes.itemArray);
        }
        this.closeDialogBox()
      } else {
        this.toastr.error(SystemConstants.ERROR, res.errMsg);
      }
    }, err => {
      if (err && err.errMsg) {
        this.toastr.error(SystemConstants.ERROR, err.errMsg);
      }
    });
  }

  /**
   * Delete category from list
  **/
  deleteUnassignedChallengeCategory(challengeCategoryId: string): void { 
    this.challengeModelService.updateChallengeCategory(challengeCategoryId, { status: CommonStatus.DELETE }).subscribe(res => {
      if (res && res.status === SystemConstants.OKAY_MSG) {
        this.localizationService.displayToastrSuccess(this.toastr, this.translations.GLOBAL.SUCCESS_MESSAGES.DELETED);
        this.getChallengeCategoryListAndCounts();
        const getIndex: number = this.challengeCategoryList.findIndex((challengeCategoryData) => String(challengeCategoryData._id) === String(challengeCategoryId));
        if (getIndex >= 0) {
          this.challengeCategoryList.splice(getIndex, 1);
        }
      } else {
        this.toastr.error(SystemConstants.ERROR, res.errMsg);
      }
    }, err => {
      if (err && err.errMsg) {
        this.toastr.error(SystemConstants.ERROR, err.errMsg);
      }
    });
  }

  openDeletePopup(challengeCategoryId: string) {
    this.sAttributes.isShow = false;
    this.closeDialogBox();
    const config = {
      backdrop: true,
      ignoreBackdropClick: true,
      class: SystemConstants.CONFIRMATION_POP_UP_CLASS,
      initialState:{
        title: SystemConstants.DELETE,
        icon: SystemConstants.CONFIRMATION_POP_UP_ICON
      }
    };
    this.modalRef = this.modalService.show(ConfirmationDialogComponent, config);
    this.modalRef.onHidden.subscribe((flagResponse: boolean) => {
      if (typeof flagResponse === SystemConstants.BOOLEAN) {
        this.deleteChallengeCategory(challengeCategoryId);
        this.modalRef.hide();
      }
    });
  }
  
  deleteChallengeCategory(challengeCategoryId: string) { 
    this.challengeModelService.ischallengeCategoryNotAssignedToAnyChallenge(challengeCategoryId).subscribe(
      (res) => {
        if (res && res.status === SystemConstants.OKAY_MSG) {
           this.deleteUnassignedChallengeCategory(challengeCategoryId)
        } else {
          this.toastr.warning(res.errMsg, SystemConstants.WARNING, { enableHtml: true });
        }
      },
      (err) => {
        if (err && err.errMsg) {
          this.toastr.error(err.errMsg, SystemConstants.ERROR);
        }
      }
    )
  }

  /**
   * Manage show/hide Category Form
  **/
  toggleForm(): void {
    if(this.searchDetails.text){
      this.isClearValue = true
    }else{
      this.isClearValue = false
      this.getChallengeCategoryListAndCounts();
    }
    this.sAttributes.isShow = !this.sAttributes.isShow;
    this.sAttributes.submitted = false;
    this.addEditCategoriesForm.reset();
    this.sAttributes.isIndex = -1;
    this.sAttributes.isActiveIdx = -1;
    this.sAttributes.itemArray = [];
    this.sAttributes.statusSelected = 1;
    // Set default radio button value
    this.addEditCategoriesForm.get('status').setValue(this.sAttributes.statusSelected);
  }

  closeDialogBox(){
    if(this.sAttributes.isIndex>= SystemConstants.ZERO_DEFAULT || this.sAttributes.isShow){
      this.sAttributes.isShow = false;
      this.sAttributes.isIndex = -1;
    }
  }

  /**
   * Get single Category filed according to index
   * @param index number
  **/
  setCategoryForEdit(index: number): void {
    setTimeout(() => {
      this.addEditCategoriesForm.reset();
      this.sAttributes.itemArray = {};
      this.sAttributes.isActiveIdx = index;
      this.sAttributes.isIndex = index;
      this.sAttributes.isShow = false;
      this.sAttributes.itemArray = this.challengeCategoryList[index];
      this.addEditCategoriesForm.patchValue(this.sAttributes.itemArray);
      this.sAttributes.statusSelected = this.sAttributes.itemArray.status;
    }, 0);
  }





  /**
   * This is used to get coupon category list by passing limit, skip
   * @param limit number
   * @param skip number
  **/
 getChallengeCategoryList(limit: number, skip: number) {
    const queryObj: IChallengeCategory = { limit, skip };
    this.route.queryParamMap.subscribe(params => {
      this.currentPage = Number(params.get(SystemConstants.PAGE)) || SystemConstants.DEFAULT_PAGE_NUMBER;
    });
    const finalQuery = this.setSearchFields(queryObj);
    this.challengeModelService.getChallengeCategoryList(finalQuery).subscribe(res => {
      if (res && res.status === SystemConstants.OKAY_MSG) {
        this.isCreatedDateVisible = this.searchDetails.date ? true : false;
        this.challengeCategoryList = res.data;
      } else {
        this.toastr.error(SystemConstants.ERROR, res.errMsg);
      }
    }, err => {
      if (err && err.error && err.error.error && err.error.error.message) {
        this.toastr.error(SystemConstants.ERROR, err.error.error.message);
      }
    });
  }


 getChallengeCategoryCounts(): void {
    const query: IChallengeCategory = {};
    const finalQuery = this.setSearchFields(query);
    this.challengeModelService.getChallengeCategoryCounts(finalQuery).subscribe(res => {
      if (res && res.status === SystemConstants.OKAY_MSG) {
        this.sAttributes.count = res.data ? res.data : 0;
        this.isNoRecordHide=true;
      }
    }, err => {
      if (err && err.error && err.error.error && err.error.error.message) {
        this.toastr.error(SystemConstants.ERROR, err.error.error.message);
      }
    });
  }

  /**
   * Used for pagination
   * @param event PageChangedEvent
  **/
  pageChanged(event: PageChangedEvent): void {
    const startItem = (event.page - 1) * event.itemsPerPage;
    this.currentPage = event.page
    this.router.navigate([], {
      queryParams: { page: this.currentPage },
      queryParamsHandling: SystemConstants.MERGE
    });
    this.sAttributes.skips= startItem;
    this.getChallengeCategoryList(this.sAttributes.limits, startItem);
    this.globalService.scrollOnTop();
  }
  resetPageUrl(){
    this.router.navigate([], {
      queryParams: { page: SystemConstants.DEFAULT_PAGE_NUMBER},
      queryParamsHandling: SystemConstants.MERGE
    });
  }

  /**
   * Copy category
  **/
  setCategoryForCopy(item: IChallengeCategory): void {
    const query = { name: this.globalService.getCopiedNameWithTimeStamp(item.name), status: item.status }
    this.challengeModelService.createChallengCategory(query).subscribe(res => {
      if (res && res.status === SystemConstants.OKAY_MSG) {
        this.localizationService.displayToastrSuccess(this.toastr, this.translations.GLOBAL.SUCCESS_MESSAGES.COPIED);
        this.getChallengeCategoryCounts();
        this.getChallengeCategoryList(this.sAttributes.limits, this.sAttributes.skips);
        const cData = res?.data?.ops?.[0] || res?.data;
        if (cData) {
          this.challengeCategoryList.splice(0, 0, cData);
        }
      } else {
        this.toastr.error(SystemConstants.ERROR, res.errMsg);
      }
    }, err => {
      if (err && err.error && err.error.error && err.error.error.message) {
        this.toastr.error(SystemConstants.ERROR, err.error.error.message);
      }
    });
  }

  getTranslation(key: string, options?: TranslationParams): string {
    return this.localizationService.getTranslation(key, options);
  }
  get active() {
    return CommonStatus.ACTIVE
  }

  get inactive() {
    return CommonStatus.INACTIVE
  }

  getCustomErrorMessage(formControl: UntypedFormControl, formControlName: string, options?: CustomValidatorOptions) {
    return this.commonValidationService.getCustomErrorMessage(formControl, formControlName, options);
  }

}
